import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@shared/ui';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { shouldRequireGoogleDriveForUser } from '../../platform/auth/userRoles';
import { analytics } from '../../services/analytics';
import { useGetGoogleConnectionStatusQuery } from '../../store/api/apiSlice';
import { useAppSelector } from '../../store/hooks';
import DriveConnectionTile from './DriveConnectionTile';

// import GitHubIntegration from './github/GitHubIntegration';
// import LinearIntegration from './linear/LinearIntegration';
// import SlackConnectionTile from './SlackConnectionTile';

const IntegrationsOverviewPage: React.FC = () => {
  const location = useLocation();
  const [flash, setFlash] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const user = useAppSelector((s) => s.auth.user);
  const { data: googleStatus } = useGetGoogleConnectionStatusQuery();
  const driveMigrationRequired =
    shouldRequireGoogleDriveForUser(user) &&
    googleStatus?.migration_required &&
    (googleStatus.legacy_providers || []).some(
      (provider) => provider.trim().toLowerCase() === 'google-drive'
    );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connectedProvider = params.get('connected');
    const connectStatus = params.get('status');

    if (connectedProvider && connectStatus) {
      const providerName =
        connectedProvider === 'github'
          ? 'GitHub'
          : connectedProvider === 'linear'
            ? 'Linear'
            : connectedProvider === 'google'
              ? 'Google'
              : connectedProvider;

      if (connectStatus === 'success') {
        setFlash({ type: 'success', text: `${providerName} account connected successfully.` });
      } else {
        setFlash({
          type: 'error',
          text: `Something went wrong connecting ${providerName}. Please try again.`,
        });
      }

      params.delete('connected');
      params.delete('status');
      params.delete('scopes');
      const newQs = params.toString();
      if (typeof window !== 'undefined') {
        const url = newQs ? `${location.pathname}?${newQs}` : location.pathname;
        window.history.replaceState({}, '', url);
      }
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    analytics.viewed(
      'Viewed settings',
      'settings',
      location.pathname,
      {},
      {
        dedupeKey: `viewed-settings:${location.key}`,
      }
    );
  }, [location.key, location.pathname]);

  // onSuccess and onError callbacks removed - they were only used by disabled integrations (GitHub, Linear)
  // If re-enabling those integrations, uncomment these:
  // const onSuccess = (text: string) => setFlash({ type: 'success', text });
  // const onError = (text: string) => setFlash({ type: 'error', text });

  return (
    <Box data-testid="integrations-overview-page" sx={{ minHeight: '100vh', py: 3 }}>
      <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Settings</Typography>
        </Box>

        {flash ? (
          <Alert
            severity={flash.type}
            onClose={() => setFlash(null)}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {flash.text}
          </Alert>
        ) : null}

        {driveMigrationRequired ? (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            Google reconnect required for Drive. Reconnect Drive to migrate it to the new Google
            OAuth client.
          </Alert>
        ) : null}

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <List component="nav" sx={{ p: 0 }}>
                <ListItemButton
                  selected
                  sx={(theme) => ({
                    borderRadius: 1.5,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: theme.extendedColors.ocean[100],
                      '& .MuiListItemText-primary': {
                        color: theme.extendedColors.ocean[600],
                        fontWeight: 600,
                      },
                    },
                  })}
                >
                  <ListItemText primary="Connections" />
                </ListItemButton>
                {/* Account link removed - this was never connected to a route and doesn't work.
                    The link pointed to /settings/account which doesn't exist in the routing configuration.
                    If account settings are needed in the future, implement the route and page first. */}
                {/* <ListItemButton
                  component={Link}
                  to="/settings/account"
                  sx={(theme) => ({
                    borderRadius: 1.5,
                    mb: 0.5,
                    '&:hover': { bgcolor: theme.extendedColors.cement[100] },
                  })}
                >
                  <ListItemText primary="Account" />
                </ListItemButton> */}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={9}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <List sx={{ p: 0 }}>
                  {/* Temporarily disabled - only Google Drive is shown per MEN-3315 */}
                  {/* <SlackConnectionTile />
                  <Divider /> */}
                  <DriveConnectionTile />
                  {/* <Divider />
                  <GitHubIntegration onSuccess={onSuccess} onError={onError} />
                  <LinearIntegration onSuccess={onSuccess} onError={onError} /> */}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default IntegrationsOverviewPage;
